import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, Panel, LoadingState, ErrorState } from "@/components/common";
import { OrderTicket } from "@/components/paper/OrderTicket";
import { KillSwitchPanel } from "@/components/paper/KillSwitchPanel";
import { PositionsTable, OrdersTable, LedgerTable } from "@/components/paper/PaperTables";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileText } from "lucide-react";
import { cn } from "@/lib/utils";

const num = (v) => Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function BookCard({ b }) {
  if (b.error) return <div className="rounded-lg border border-amber-500/30 p-4 text-xs text-amber-300" data-testid={`book-${b.ccy}`}>{b.ccy}: {b.error}</div>;
  const rows = [["Cash", b.cash], ["Market value", b.market_value], ["Realized P&L", b.realized_pnl, true], ["Unrealized P&L", b.unrealized_pnl, true],
    ["Today", b.daily_pnl, true], ["Fees", b.fees], ["Exposure", `${b.exposure_pct}%`], ["Drawdown", `${b.drawdown_pct}%`]];
  return (
    <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--card)] p-4" data-testid={`book-${b.ccy}`}>
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-[11px] uppercase tracking-wide text-slate-500">{b.ccy === "INR" ? "Sharekhan book (INR)" : "Binance book (USDT)"}</span>
        <span className="font-mono-num text-lg font-bold text-slate-100" data-testid={`book-${b.ccy}-equity`}>{num(b.equity)}</span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">{rows.map(([l, v, signed]) => (
        <div key={l} className="flex justify-between text-[11px]"><span className="text-slate-500">{l}</span>
          <span className={cn("font-mono-num", signed ? (Number(v) >= 0 ? "text-emerald-400" : "text-rose-400") : "text-slate-300")}>{String(v).endsWith("%") ? v : num(v)}</span></div>))}</div>
    </div>
  );
}

export default function PaperTrading({ defaultTab = "positions" }) {
  const [tab, setTab] = useState(defaultTab);
  const [ccy, setCcy] = useState("INR");
  const sum = useQuery({ queryKey: ["paper", "summary"], refetchInterval: 10000, queryFn: async () => (await api.get("/paper/summary")).data });
  const orders = useQuery({ queryKey: ["paper", "orders"], refetchInterval: 10000, queryFn: async () => (await api.get("/paper/orders")).data });
  const ledger = useQuery({ queryKey: ["paper", "ledger", ccy], enabled: tab === "ledger", queryFn: async () => (await api.get("/paper/ledger", { params: { ccy } })).data });
  if (sum.isLoading) return <LoadingState label="Loading paper trading..." />;
  if (sum.isError) return <ErrorState onRetry={sum.refetch} />;
  const books = Object.values(sum.data.books);
  const positions = books.flatMap((b) => b.positions || []);
  return (
    <div>
      <PageHeader title="Paper Trading" subtitle="Simulated execution · every order passes the 9 risk rules · live trading disabled" icon={FileText} testId="paper-header" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">{books.map((b) => <BookCard key={b.ccy} b={b} />)}</div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <Panel title="Activity" testId="paper-activity-panel">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="m-3" data-testid="paper-tabs">
                <TabsTrigger value="positions" data-testid="tab-positions">Positions ({positions.length})</TabsTrigger>
                <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
                <TabsTrigger value="ledger" data-testid="tab-ledger">Paper ledger</TabsTrigger>
              </TabsList>
              <TabsContent value="positions"><PositionsTable positions={positions} /></TabsContent>
              <TabsContent value="orders"><OrdersTable orders={orders.data?.items || []} /></TabsContent>
              <TabsContent value="ledger">
                <div className="px-3 flex gap-2">{["INR", "USDT"].map((c) => (
                  <button key={c} data-testid={`ledger-ccy-${c}`} onClick={() => setCcy(c)} className={cn("px-2 py-1 rounded text-[11px]", ccy === c ? "bg-blue-500/20 text-blue-300" : "text-slate-400")}>{c}</button>))}</div>
                {ledger.isLoading ? <LoadingState /> : <LedgerTable ledger={ledger.data} />}
              </TabsContent>
            </Tabs>
          </Panel>
        </div>
        <div className="space-y-4">
          <KillSwitchPanel engaged={sum.data.kill_switch_engaged} />
          <OrderTicket disabled={sum.data.kill_switch_engaged} />
          <p className="text-[11px] text-slate-500" data-testid="paper-fixture-note">{sum.data.settings.note} Fees {sum.data.settings.fee_bps.sharekhan}/{sum.data.settings.fee_bps.binance} bps, slippage {sum.data.settings.slippage_bps.sharekhan}/{sum.data.settings.slippage_bps.binance} bps (Sharekhan/Binance). Stock prices are simulated until Sharekhan data is connected.</p>
        </div>
      </div>
    </div>
  );
}
